import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractApiErrorMessage } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { CheckCircle2, KeyRound } from "lucide-react";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("كلمة المرور يجب أن تكون ٦ أحرف فأكثر");
      return;
    }
    if (password !== confirm) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("تم تحديث كلمة المرور بنجاح");
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "تعذّر إعادة التعيين"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <PageShell>
        <section className="max-w-md mx-auto px-6 py-16">
          <div className="card-warm p-8 text-center">
            <h1 className="font-heading text-3xl text-[#222222]">الرابط غير مكتمل</h1>
            <p className="text-[#5E5C58] mt-3">
              يرجى استخدام الرابط المرسل إلى بريدك الإلكتروني.
            </p>
            <Link to="/forgot-password" className="mt-5 inline-block">
              <Button className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white">
                طلب رابط جديد
              </Button>
            </Link>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
        <div className="card-warm p-8">
          {!done ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#F6F4EE] flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[#0A4D3C]" />
                </div>
                <h1 className="font-heading text-3xl text-[#222222]">كلمة مرور جديدة</h1>
              </div>
              <p className="text-[#5E5C58] text-sm mt-3">
                اختر كلمة مرور قويّة (٦ أحرف فأكثر). يمكنك إظهار النص بالضغط على أيقونة العين.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="password">كلمة المرور الجديدة</Label>
                  <PasswordInput
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2"
                    data-testid="reset-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm">تأكيد كلمة المرور</Label>
                  <PasswordInput
                    id="confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="mt-2"
                    data-testid="reset-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-11"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "جاري الحفظ..." : "تحديث كلمة المرور"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-success-state">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
              <h1 className="font-heading text-3xl text-[#222222] mt-4">تم التحديث 🌿</h1>
              <p className="text-[#5E5C58] mt-3">
                أصبح بإمكانك الدخول إلى حسابك بكلمة المرور الجديدة. سننقلك لصفحة تسجيل الدخول خلال لحظات...
              </p>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};

export default ResetPassword;
